import * as React from "react";

interface HostValue {
  host: HTMLElement | null;
  setHost: (el: HTMLElement | null) => void;
  /** Mode Modifier de la sidebar. Source de vérité pour activer/désactiver
   *  l'édition docking du module éditable courant. */
  sidebarEditMode: boolean;
  setSidebarEditMode: (v: boolean) => void;
  /** Id d'un module fraîchement créé : le RenameField correspondant doit
   *  prendre le focus + sélectionner son contenu au prochain montage. */
  pendingRenameModuleId: string | null;
  setPendingRenameModuleId: (id: string | null) => void;
}

const HostCtx = React.createContext<HostValue | null>(null);

export function DockingEditBarHostProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [host, setHost] = React.useState<HTMLElement | null>(null);
  const [sidebarEditMode, setSidebarEditMode] = React.useState(false);
  const [pendingRenameModuleId, setPendingRenameModuleId] = React.useState<
    string | null
  >(null);
  const value = React.useMemo(
    () => ({
      host,
      setHost,
      sidebarEditMode,
      setSidebarEditMode,
      pendingRenameModuleId,
      setPendingRenameModuleId,
    }),
    [host, sidebarEditMode, pendingRenameModuleId],
  );
  return <HostCtx.Provider value={value}>{children}</HostCtx.Provider>;
}

export function useDockingEditBarHost(): HTMLElement | null {
  const ctx = React.useContext(HostCtx);
  return ctx?.host ?? null;
}

export function useSidebarEditMode(): {
  sidebarEditMode: boolean;
  setSidebarEditMode: (v: boolean) => void;
} {
  const ctx = React.useContext(HostCtx);
  return {
    sidebarEditMode: ctx?.sidebarEditMode ?? false,
    setSidebarEditMode: ctx?.setSidebarEditMode ?? (() => undefined),
  };
}

export function usePendingRename(): {
  pendingRenameModuleId: string | null;
  setPendingRenameModuleId: (id: string | null) => void;
} {
  const ctx = React.useContext(HostCtx);
  return {
    pendingRenameModuleId: ctx?.pendingRenameModuleId ?? null,
    setPendingRenameModuleId:
      ctx?.setPendingRenameModuleId ?? (() => undefined),
  };
}

/**
 * Marque le DOM target où le bandeau d'édition docking doit se rendre.
 * À monter une seule fois (typiquement sous le module actif dans la sidebar).
 */
export function DockingEditBarSlot({ className }: { className?: string }) {
  const ctx = React.useContext(HostCtx);
  if (!ctx) return null;
  return <div ref={ctx.setHost} className={className} />;
}
